import type { ComputedRef } from "vue"
import { computed, provide } from "vue"

/**
 * Injection key crossroads' `aiTag` mixin reads (see @jobshop/crossroads
 * components/GlobalUI/mixins/aiTag.js).
 */
export const AI_GENERATED_LABEL_KEY = "crAiGeneratedLabel"

export const AI_GENERATED_LABEL_TRANSLATION_KEY = "ai.generated-image"

/**
 * Provides the AI-generated media disclosure wording (EU AI Act Art. 50(4)) to
 * every crossroads module in the subtree.
 *
 * Crossroads deliberately ships no wording of its own — translations live here.
 * Every module also accepts an `ai-label` prop, but providing it once means a
 * module or call site added later is covered instead of silently losing its
 * disclosure.
 *
 * Call this before any `await` in the consuming `setup()`: `provide()` needs the
 * active component instance and no-ops once setup has been suspended, which
 * would drop the tag from every image without failing loudly.
 *
 * @param t - translate function, passed in rather than resolved via `useI18n()`
 * internally so that constraint stays visible at the call site (see AGENTS.md).
 * Resolves against the active i18n locale, like the other crossroads module
 * labels.
 * @returns The reactive label, for call sites that also need it directly.
 */
export function provideCrossroadsAiLabel(t: (key: string) => string): ComputedRef<string> {
  const label = computed(() => t(AI_GENERATED_LABEL_TRANSLATION_KEY))

  provide(AI_GENERATED_LABEL_KEY, label)

  return label
}
